import {
  forwardRef,
  type InputHTMLAttributes,
  type MutableRefObject,
  type ForwardedRef,
} from 'react';
import { Input as AntInput } from 'antd';
import type { InputRef } from 'antd';
import { useController, useFormContext, type Control } from 'react-hook-form';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> & {
  invalid?: boolean;
  type?: string;
};

/**
 * Antd's Input keeps its own internal `useState` — when react-hook-form writes
 * a value via ref (uncontrolled mode), antd overwrites it on the next render.
 * That looks to the user as "data from the backend never shows up after
 * refresh". So whenever an Input sits inside a FormProvider and carries a
 * `name`, we promote it to controlled mode via useController. Outside of a
 * form context we keep the lightweight uncontrolled antd render.
 */

function bridgeRef(ref: ForwardedRef<HTMLInputElement>) {
  return (node: InputRef | null) => {
    const el = node?.input ?? null;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as MutableRefObject<HTMLInputElement | null>).current = el;
  };
}

const PlainInput = forwardRef<HTMLInputElement, InputProps>(
  ({ invalid, type = 'text', className, ...props }, ref) => {
    const setRef = bridgeRef(ref);
    if (type === 'password') {
      return (
        <AntInput.Password
          ref={setRef}
          status={invalid ? 'error' : undefined}
          className={className}
          {...(props as never)}
        />
      );
    }
    return (
      <AntInput
        ref={setRef}
        type={type}
        status={invalid ? 'error' : undefined}
        className={className}
        {...(props as never)}
      />
    );
  },
);
PlainInput.displayName = 'PlainInput';

interface ControlledInputProps extends InputProps {
  name: string;
  control: Control;
}

const ControlledInput = forwardRef<HTMLInputElement, ControlledInputProps>(
  ({ name, control, invalid, type = 'text', className, ...rest }, ref) => {
    const { field, fieldState } = useController({ name, control });
    const status = invalid || fieldState.error ? 'error' : undefined;
    const setRef = bridgeRef(ref);
    if (type === 'password') {
      return (
        <AntInput.Password
          ref={setRef}
          name={field.name}
          value={(field.value ?? '') as string}
          onChange={field.onChange}
          onBlur={field.onBlur}
          status={status}
          className={className}
          {...(rest as never)}
        />
      );
    }
    return (
      <AntInput
        ref={setRef}
        type={type}
        name={field.name}
        value={(field.value ?? '') as string}
        onChange={field.onChange}
        onBlur={field.onBlur}
        status={status}
        className={className}
        {...(rest as never)}
      />
    );
  },
);
ControlledInput.displayName = 'ControlledInput';

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const ctx = useFormContext();
  if (props.name && ctx?.control) {
    return <ControlledInput {...props} name={props.name} control={ctx.control} ref={ref} />;
  }
  return <PlainInput {...props} ref={ref} />;
});
Input.displayName = 'Input';
